// frontend/src/ui/CartModal.jsx
import React, { useEffect, useState, useMemo, Suspense } from "react";
import { useNavigate } from "react-router-dom";
import { Modal, Button } from "./UIComponents";
import HDSD_DATA from "../pages/hdsd.json";

const BulkPackingModal = React.lazy(() => import("./BulkPackingModal"));

export default function CartModal({ open, onClose }) {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [filterRef, setFilterRef] = useState(""); 
  
  const [isPurchaser, setIsPurchaser] = useState(false); 
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [itemsToPack, setItemsToPack] = useState([]);
  const [estimating, setEstimating] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);

  // --- Sales Attribution & Management States ---
  const [salesStaff, setSalesStaff] = useState([]);
  const [selectedStaffForRef, setSelectedStaffForRef] = useState("");
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportMonth, setReportMonth] = useState("all");
  const [syncingAppUser, setSyncingAppUser] = useState(false);
  const [expandedStaff, setExpandedStaff] = useState(new Set());
  const [excelExportModalOpen, setExcelExportModalOpen] = useState(false);

  const toggleExpandStaff = (staffKey) => {
    setExpandedStaff((prev) => {
      const next = new Set(prev);
      if (next.has(staffKey)) next.delete(staffKey);
      else next.add(staffKey);
      return next;
    });
  };

  const handleToggleExpandAll = () => {
    if (!reportData?.rows) return;
    if (expandedStaff.size === reportData.rows.length) {
      setExpandedStaff(new Set());
    } else {
      const allKeys = new Set(reportData.rows.map((r) => r.ma_nhan_su !== "N/A" ? r.ma_nhan_su : r.salesperson_name));
      setExpandedStaff(allKeys);
    }
  };

  // Dedicated PDF Export Engine following the active filtered month
  const handleExportPDF = () => {
    if (!reportData) return;
    const printWindow = window.open("", "_blank");
    if (!printWindow) {
      alert("Vui lòng cho phép mở popup để xuất file PDF / Please allow popups to export PDF.");
      return;
    }

    const monthTitle = reportMonth === "all" ? "Tất cả các tháng / All Months" : `Tháng ${reportMonth} (Month ${reportMonth}, 2026)`;
    const nowStr = new Date().toLocaleString("vi-VN");

    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8" />
        <title>Bao_cao_chuyen_doi_${reportMonth}_${Date.now()}</title>
        <style>
          @page { size: A4 portrait; margin: 12mm 10mm; }
          body { font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; font-size: 11px; color: #1e293b; margin: 0; padding: 12px; }
          h2 { margin: 0 0 4px; font-size: 17px; color: #0f172a; text-transform: uppercase; }
          .meta { font-size: 11px; color: #64748b; margin-bottom: 14px; }
          .kpi-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; margin-bottom: 16px; }
          .kpi-card { border: 1px solid #cbd5e1; border-radius: 6px; padding: 10px; background: #f8fafc; }
          .kpi-label { font-size: 10px; font-weight: 700; color: #475569; text-transform: uppercase; }
          .kpi-val { font-size: 17px; font-weight: 700; color: #0f172a; margin-top: 4px; }
          table { width: 100%; border-collapse: collapse; margin-bottom: 14px; }
          th { background: #f1f5f9; border: 1px solid #cbd5e1; padding: 6px 8px; text-align: left; font-size: 10.5px; }
          td { border: 1px solid #e2e8f0; padding: 6px 8px; font-size: 10.5px; }
          .sub-table { width: 96%; margin: 6px auto; background: #fafafa; border: 1px solid #e2e8f0; }
          .sub-table th { background: #e2e8f0; font-size: 9.5px; padding: 4px 6px; }
          .sub-table td { font-size: 9.5px; padding: 4px 6px; }
          .badge-sold { background: #dcfce7; color: #166534; font-weight: 700; padding: 2px 5px; border-radius: 3px; font-size: 9px; }
          .badge-quote { background: #f1f5f9; color: #475569; font-weight: 600; padding: 2px 5px; border-radius: 3px; font-size: 9px; }
          .notes { font-size: 10px; color: #92400e; background: #fffbeb; border: 1px solid #fef3c7; border-radius: 6px; padding: 8px 12px; line-height: 1.4; }
          @media print {
            body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            .avoid-break { page-break-inside: avoid; }
          }
        </style>
      </head>
      <body>
        <h2>TUISS WEB TOOLS — BÁO CÁO TỶ LỆ CHUYỂN ĐỔI BÁO GIÁ</h2>
        <div class="meta">
          <span><b>Kỳ báo cáo:</b> ${monthTitle}</span> &nbsp;|&nbsp; 
          <span><b>Thời gian xuất:</b> ${nowStr}</span> &nbsp;|&nbsp; 
          <span><b>Định giá:</b> Factory Production Cost only (Exwork + Ops_SC) * Qty [ship_sc excluded]</span>
        </div>

        <div class="kpi-grid">
          <div class="kpi-card">
            <div class="kpi-label">Tổng Báo Giá / Total Quoted</div>
            <div class="kpi-val">$${(reportData.summary.total_quoted_value || 0).toLocaleString()}</div>
            <div style="font-size: 9px; color: #64748b;">${reportData.summary.total_quotes} yêu cầu báo giá</div>
          </div>
          <div class="kpi-card" style="background: #f0fdf4; border-color: #bbf7d0;">
            <div class="kpi-label" style="color: #166534;">Doanh Số Đã Bán / Total Sold</div>
            <div class="kpi-val" style="color: #15803d;">$${(reportData.summary.total_sold_value || 0).toLocaleString()}</div>
            <div style="font-size: 9px; color: #16a34a;">${reportData.summary.sold_quotes} đơn chốt thành công</div>
          </div>
          <div class="kpi-card" style="background: #eff6ff; border-color: #bfdbfe;">
            <div class="kpi-label" style="color: #1e40af;">Win Rate (Số lượng)</div>
            <div class="kpi-val" style="color: #2563eb;">${reportData.summary.win_rate_vol_pct}%</div>
            <div style="font-size: 9px; color: #3b82f6;">Tỷ lệ chốt trên tổng báo giá</div>
          </div>
          <div class="kpi-card" style="background: #faf5ff; border-color: #e9d5ff;">
            <div class="kpi-label" style="color: #6b21a8;">Revenue Yield (Giá trị)</div>
            <div class="kpi-val" style="color: #9333ea;">${reportData.summary.revenue_yield_val_pct}%</div>
            <div style="font-size: 9px; color: #a855f7;">Hiệu suất giá trị sản xuất</div>
          </div>
        </div>

        <table>
          <thead>
            <tr>
              <th>Nhân viên Kinh doanh</th>
              <th>Mã NS (CRM)</th>
              <th style="text-align: center;">Số đơn (Chốt / Tổng)</th>
              <th style="text-align: right;">Win Rate (%)</th>
              <th style="text-align: right;">Tổng Báo Giá (USD)</th>
              <th style="text-align: right;">Đã Bán (USD)</th>
              <th style="text-align: right;">Revenue Yield (%)</th>
            </tr>
          </thead>
          <tbody>
            ${reportData.rows.map((row) => `
              <tr class="avoid-break" style="background: #f8fafc; font-weight: bold;">
                <td>${row.salesperson_name}</td>
                <td>${row.ma_nhan_su}</td>
                <td style="text-align: center;"><span style="color: #166534;">${row.sold_quotes}</span> /${row.total_quotes}</td>
                <td style="text-align: right; color: #2563eb;">${row.win_rate_vol_pct}%</td>
                <td style="text-align: right;">$${row.total_quoted_value.toLocaleString()}</td>                 <td style="text-align: right; color: #15803d;">$${row.total_sold_value.toLocaleString()}</td>
                <td style="text-align: right; color: #9333ea;">${row.revenue_yield_val_pct}%</td>
              </tr>
              ${row.quotes && row.quotes.length > 0 ? `
                <tr class="avoid-break">
                  <td colspan="7" style="padding: 4px 8px 12px 24px; background: #fff;">
                    <table class="sub-table">
                      <thead>
                        <tr>
                          <th style="width: 25px;">#</th>
                          <th>Mã Báo Giá / Order Ref</th>
                          <th style="width: 80px;">Ngày Tạo</th>
                          <th style="width: 55px; text-align: center;">Số dòng</th>
                          <th style="width: 100px; text-align: center;">Trạng thái</th>
                          <th style="width: 110px; text-align: right;">Báo Giá (USD)</th>
                          <th style="width: 110px; text-align: right;">Đã Bán (USD)</th>
                        </tr>
                      </thead>
                      <tbody>
                        ${row.quotes.map((q, qIdx) => `
                          <tr>
                            <td>${qIdx + 1}</td>
                            <td><b>${q.order_ref}</b></td>
                            <td>${q.quote_date ? String(q.quote_date).slice(0, 10) : "—"}</td>
                            <td style="text-align: center;">${q.lines_count}</td>
                            <td style="text-align: center;">
                              ${q.is_sold ? '<span class="badge-sold">✓ Đã bán</span>' : '<span class="badge-quote">Đang báo giá</span>'}                             </td>                             <td style="text-align: right;">$${q.quoted_value.toLocaleString()}</td>
                            <td style="text-align: right; font-weight: bold; color: ${q.is_sold ? '#15803d' : '#888'};">                               $${q.sold_value.toLocaleString()}
                            </td>
                          </tr>
                        `).join("")}
                      </tbody>
                    </table>
                  </td>
                </tr>
              ` : ""}
            `).join("")}
          </tbody>
        </table>

        <div class="notes avoid-break">
          <b>📋 Ghi chú vận hành quản lý:</b><br/>
          • <b>Win Rate:</b> Tỷ lệ phần trăm số lượng báo giá được chốt thành đơn hàng thực tế (<code>sold = true</code>) trên tổng số lượt khách gửi yêu cầu báo giá.<br/>
          • <b>Revenue Yield:</b> Tỷ lệ phần trăm giữa tổng giá trị sản xuất thực tế chốt bán thành công so với tổng giá trị sản xuất đã báo giá ban đầu.<br/>
          • <b>Định giá chuẩn sản xuất:</b> Giá trị sản phẩm chỉ ghi nhận giá trị xuất xưởng thực tế tại nhà máy <code>(exwork + ops_sc) * qty</code>. Toàn bộ chi phí cước vận chuyển (<code>ship_sc</code>) được tách biệt hoàn toàn.
        </div>

        <script>
          window.onload = function() {
            window.focus();
            window.print();
          };
        </script>
      </body>
      </html>
    `;

    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
  };

  // Dedicated Excel Export Engine following the active filtered month (Hierarchical: Sales -> Quote -> Line Items)
  const handleExportExcel = (includeDetails = true) => {
    if (!reportData) return;
    const monthTitle = reportMonth === "all" 
      ? "Tất cả các tháng / All Months" 
      : `Tháng ${reportMonth} (Month ${reportMonth}, 2026)`;
    const filenameMonth = reportMonth === "all" ? "Tat_ca_cac_thang" : `Thang_${reportMonth}`;
    const modeSuffix = includeDetails ? "Chi_tiet" : "Tong_hop";
    const nowStr = new Date().toLocaleString("vi-VN");
    const s = reportData.summary || {};

    let tableRowsHtml = "";
    (reportData.rows || []).forEach((row) => {
      // 1. Salesperson Summary Row
      tableRowsHtml += `
        <tr style="background-color: #f1f5f9; font-weight: bold;">
          <td style="border: 1px solid #cbd5e1; padding: 7px 10px; font-size: 11pt;">${row.salesperson_name}</td>
          <td style="border: 1px solid #cbd5e1; padding: 7px 10px; font-size: 10pt; color: #475569;">${row.ma_nhan_su}</td>
          <td style="border: 1px solid #cbd5e1; padding: 7px 10px; text-align: center;">${row.sold_quotes} / ${row.total_quotes}</td>
          <td style="border: 1px solid #cbd5e1; padding: 7px 10px; text-align: right; color: #2563eb;">${row.win_rate_vol_pct}%</td>
          <td style="border: 1px solid #cbd5e1; padding: 7px 10px; text-align: right; mso-number-format:'\\$\\#\\,\\#\\#0\\.00';">$${Number(row.total_quoted_value || 0).toLocaleString()}</td>
          <td style="border: 1px solid #cbd5e1; padding: 7px 10px; text-align: right; color: #15803d; mso-number-format:'\\$\\#\\,\\#\\#0\\.00';">$${Number(row.total_sold_value || 0).toLocaleString()}</td>
          <td style="border: 1px solid #cbd5e1; padding: 7px 10px; text-align: right; color: #9333ea;">${row.revenue_yield_val_pct}%</td>
        </tr>
      `;

      if (row.quotes && row.quotes.length > 0) {
        row.quotes.forEach((q, qIdx) => {
          const dateStr = q.quote_date ? String(q.quote_date).slice(0, 10) : "—";
          const statusTxt = q.is_sold ? "✓ Đã bán / Sold" : "Đang báo giá";
          const statusBg = q.is_sold ? "#dcfce7" : "#fff7ed";
          const statusColor = q.is_sold ? "#166534" : "#c2410c";

          // 2. Quote Reference Summary Row (Spanning 7 columns cleanly)
          tableRowsHtml += `
            <tr style="background-color: #e2e8f0; font-weight: bold; font-size: 10pt;">
              <td style="border: 1px solid #cbd5e1; padding: 5px 8px; text-align: center; color: #475569;">${qIdx + 1}</td>
              <td colspan="2" style="border: 1px solid #cbd5e1; padding: 5px 8px;">Mã Báo Giá: <b>${q.order_ref}</b></td>
              <td style="border: 1px solid #cbd5e1; padding: 5px 8px; text-align: center; mso-number-format:'yyyy-mm-dd';">${dateStr}</td>
              <td style="border: 1px solid #cbd5e1; padding: 5px 8px; text-align: center;">${q.lines_count} dòng</td>
              <td style="border: 1px solid #cbd5e1; padding: 5px 8px; text-align: center; background-color: ${statusBg}; color: ${statusColor}; font-weight: bold;">${statusTxt}</td>
              <td style="border: 1px solid #cbd5e1; padding: 5px 8px; text-align: right; mso-number-format:'\\$\\#\\,\\#\\#0\\.00';">$${Number(q.quoted_value || 0).toLocaleString()}</td>
            </tr>
          `;

          // 3. Line Items Header & Data Rows (Conditionally appended when includeDetails is true)
          if (includeDetails && q.items && q.items.length > 0) {
            tableRowsHtml += `
              <tr style="background-color: #f8fafc; font-weight: 600; font-size: 9pt; color: #475569;">
                <td style="border: 1px solid #e2e8f0; padding: 4px 6px; text-align: center;">Dòng</td>
                <td style="border: 1px solid #e2e8f0; padding: 4px 6px;">Sản phẩm / Product</td>
                <td style="border: 1px solid #e2e8f0; padding: 4px 6px;">Collection & Màu / Color</td>
                <td style="border: 1px solid #e2e8f0; padding: 4px 6px;">Vận hành / Ops</td>
                <td style="border: 1px solid #e2e8f0; padding: 4px 6px; text-align: center;">Kích thước (R x C mm)</td>
                <td style="border: 1px solid #e2e8f0; padding: 4px 6px; text-align: center;">SL</td>
                <td style="border: 1px solid #e2e8f0; padding: 4px 6px; text-align: right;">Thành tiền (USD)</td>
              </tr>
            `;

            q.items.forEach((item, iIdx) => {
              tableRowsHtml += `
                <tr style="font-size: 9pt; background-color: #ffffff;">
                  <td style="border: 1px solid #e2e8f0; padding: 4px 6px; text-align: center; color: #888;">${qIdx + 1}.${iIdx + 1}</td>
                  <td style="border: 1px solid #e2e8f0; padding: 4px 6px; font-weight: 500;">${item.prod_label}</td>
                  <td style="border: 1px solid #e2e8f0; padding: 4px 6px; color: #555;">${item.fabric_display}</td>
                  <td style="border: 1px solid #e2e8f0; padding: 4px 6px; color: #0284c7;">${item.prod_ops}</td>
                  <td style="border: 1px solid #e2e8f0; padding: 4px 6px; text-align: center;">${item.dimensions}</td>
                  <td style="border: 1px solid #e2e8f0; padding: 4px 6px; text-align: center;">${item.qty}</td>
                  <td style="border: 1px solid #e2e8f0; padding: 4px 6px; text-align: right; mso-number-format:'\\$\\#\\,\\#\\#0\\.00';">$${Number(item.line_total || 0).toLocaleString()}</td>
                </tr>
              `;
            });
          }
        });
      }
    });

    const excelTemplate = `
      <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
      <head>
        <meta charset="utf-8" />
        <!--[if gte mso 9]>
        <xml>
          <x:ExcelWorkbook>
            <x:ExcelWorksheets>
              <x:ExcelWorksheet>
                <x:Name>Bao_cao_chuyen_doi</x:Name>
                <x:WorksheetOptions>
                  <x:DisplayGridlines/>
                </x:WorksheetOptions>
              </x:ExcelWorksheet>
            </x:ExcelWorksheets>
          </x:ExcelWorkbook>
        </xml>
        <![endif]-->
        <style>
          body { font-family: Calibri, 'Segoe UI', Arial, sans-serif; font-size: 11pt; color: #1e293b; }
          table { border-collapse: collapse; width: 100%; margin-bottom: 16px; }
          th { background-color: #0f766e; color: #ffffff; font-weight: bold; border: 1px solid #cbd5e1; padding: 8px 10px; text-align: left; }
          td { border: 1px solid #cbd5e1; padding: 6px 10px; }
        </style>
      </head>
      <body>
        <table>
          <tr><td colspan="7" style="font-size: 16pt; font-weight: bold; color: #0f172a; border: none;">TUISS WEB TOOLS — BÁO CÁO TỶ LỆ CHUYỂN ĐỔI BÁO GIÁ</td></tr>
          <tr><td colspan="7" style="font-size: 10.5pt; color: #64748b; border: none;">Kỳ báo cáo: <b>${monthTitle}</b> | Thời gian xuất: ${nowStr}</td></tr>
          <tr><td colspan="7" style="font-size: 10.5pt; color: #64748b; border: none;">Định giá: Factory Production Cost only (Exwork + Ops_SC) * Qty [ship_sc excluded]</td></tr>
          <tr><td colspan="7" style="border: none;"></td></tr>
        </table>

        <table>
          <tr style="background-color: #f1f5f9; font-weight: bold; font-size: 10pt; color: #334155;">
            <td colspan="2" style="border: 1px solid #cbd5e1; padding: 8px;">TỔNG BÁO GIÁ / TOTAL QUOTED</td>
            <td colspan="2" style="border: 1px solid #bbf7d0; padding: 8px; background-color: #f0fdf4; color: #166534;">DOANH SỐ ĐÃ BÁN / TOTAL SOLD</td>
            <td style="border: 1px solid #bfdbfe; padding: 8px; background-color: #eff6ff; color: #1e40af;">WIN RATE (SỐ LƯỢNG)</td>
            <td colspan="2" style="border: 1px solid #e9d5ff; padding: 8px; background-color: #faf5ff; color: #6b21a8;">REVENUE YIELD (GIÁ TRỊ)</td>
          </tr>
          <tr style="font-size: 14pt; font-weight: bold;">
            <td colspan="2" style="border: 1px solid #cbd5e1; padding: 8px; mso-number-format:'\\$\\#\\,\\#\\#0\\.00';">$${Number(s.total_quoted_value || 0).toLocaleString()}</td>
            <td colspan="2" style="border: 1px solid #bbf7d0; padding: 8px; background-color: #f0fdf4; color: #15803d; mso-number-format:'\\$\\#\\,\\#\\#0\\.00';">$${Number(s.total_sold_value || 0).toLocaleString()}</td>
            <td style="border: 1px solid #bfdbfe; padding: 8px; background-color: #eff6ff; color: #2563eb;">${s.win_rate_vol_pct || 0}%</td>
            <td colspan="2" style="border: 1px solid #e9d5ff; padding: 8px; background-color: #faf5ff; color: #9333ea;">${s.revenue_yield_val_pct || 0}%</td>
          </tr>
          <tr style="font-size: 9pt; color: #64748b;">
            <td colspan="2" style="border: 1px solid #cbd5e1; padding: 4px 8px;">${s.total_quotes || 0} yêu cầu báo giá</td>
            <td colspan="2" style="border: 1px solid #bbf7d0; padding: 4px 8px; background-color: #f0fdf4; color: #16a34a;">${s.sold_quotes || 0} đơn chốt thành công</td>
            <td style="border: 1px solid #bfdbfe; padding: 4px 8px; background-color: #eff6ff; color: #3b82f6;">Tỷ lệ chốt trên tổng báo giá</td>
            <td colspan="2" style="border: 1px solid #e9d5ff; padding: 4px 8px; background-color: #faf5ff; color: #a855f7;">Hiệu suất giá trị sản xuất</td>
          </tr>
          <tr><td colspan="7" style="border: none;"></td></tr>
        </table>

        <table>
          <thead>
            <tr>
              <th style="width: 220px;">Nhân viên Kinh doanh</th>
              <th style="width: 140px;">Mã NS (CRM)</th>
              <th style="width: 130px; text-align: center;">Số đơn (Chốt / Tổng)</th>
              <th style="width: 110px; text-align: right;">Win Rate (%)</th>
              <th style="width: 160px; text-align: right;">Tổng Báo Giá (USD)</th>
              <th style="width: 150px; text-align: right;">Đã Bán (USD)</th>
              <th style="width: 130px; text-align: right;">Revenue Yield (%)</th>
            </tr>
          </thead>
          <tbody>
            ${tableRowsHtml}
          </tbody>
        </table>

        <table>
          <tr><td colspan="7" style="border: none;"></td></tr>
          <tr style="background-color: #fffbeb; color: #92400e; font-size: 9.5pt;">
            <td colspan="7" style="border: 1px solid #fef3c7; padding: 8px 12px; line-height: 1.4;">
              <b>📋 Ghi chú vận hành quản lý:</b><br/>
              • <b>Win Rate:</b> Tỷ lệ phần trăm số lượng báo giá được chốt thành đơn hàng thực tế (sold = true) trên tổng số lượt khách gửi yêu cầu báo giá.<br/>
              • <b>Revenue Yield:</b> Tỷ lệ phần trăm giữa tổng giá trị sản xuất thực tế chốt bán thành công so với tổng giá trị sản xuất đã báo giá ban đầu.<br/>
              • <b>Định giá chuẩn sản xuất:</b> Giá trị sản phẩm chỉ ghi nhận giá trị xuất xưởng thực tế tại nhà máy (exwork + ops_sc) * qty. Toàn bộ chi phí cước vận chuyển (ship_sc) được tách biệt hoàn toàn.
            </td>
          </tr>
        </table>
      </body>
      </html>
    `;

    const blob = new Blob(["\ufeff" + excelTemplate], {
      type: "application/vnd.ms-excel;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Bao_cao_chuyen_doi_${filenameMonth}_${modeSuffix}_${Date.now()}.xls`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // --- Embedded Standalone Accessories Feature States ---
  const [accessoryOpen, setAccessoryOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [matchingProducts, setMatchingProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [selectedAccessory, setSelectedAccessory] = useState(null);
  const [accessoryQty, setAccessoryQty] = useState(1);
  const [actionError, setActionError] = useState("");

  // --- Inline Exwork Price Editing States ---
  const [isEditingExwork, setIsEditingExwork] = useState(false);
  const [editedExworkMap, setEditedExworkMap] = useState({});

  const handleSaveExwork = async () => {
    setLoading(true);
    try {
      const updates = Object.entries(editedExworkMap).map(([id, price]) =>
        fetch("/api/data", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            scope: "order_log",
            action: "update_exwork",
            order_id: Number(id),
            exwork: Number(price) || 0
          })
        })
      );
      await Promise.all(updates);
      setIsEditingExwork(false);
      setEditedExworkMap({});
      await loadCart();
    } catch (err) {
      alert("Lỗi khi cập nhật giá Exwork: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const activeGuide = HDSD_DATA["cartmodal"] || HDSD_DATA.default;

  useEffect(() => {
    if (open) {
      loadCart();
      loadSalesStaff();
    }
  }, [open]);

  // Handle debounced product lookups dynamically as the user types
  useEffect(() => {
    if (!accessoryOpen) return;
    const delayDebounce = setTimeout(() => {
      fetchAccessoryCatalog(searchQuery);
    }, 250);
    return () => clearTimeout(delayDebounce);
  }, [searchQuery, accessoryOpen]);

  const loadSalesStaff = async () => {
    try {
      const res = await fetch("/api/data?scope=staff&sales_only=1");
      const data = await res.json();
      if (data.ok) {
        setSalesStaff(data.rows || []);
      }
    } catch (err) {
      console.error("Failed to load sales staff:", err);
    }
  };

  const loadCart = async () => {
    setLoading(true);
    try {
      const timestamp = Date.now();
      const res = await fetch(`/api/data?scope=order_log&action=list&t=${timestamp}`, {
        method: "GET",
        cache: "no-store",
        headers: {
          "Pragma": "no-cache",
          "Cache-Control": "no-cache"
        }
      });
      
      const data = await res.json();
      if (data.ok) {
        setRows([]); 
        setTimeout(() => {
          setRows(data.rows || []);
        }, 0);

        if (typeof data.is_purchaser !== 'undefined') {
            setIsPurchaser(data.is_purchaser);
        }
      }
    } catch (e) {
      console.error("Cart reload failed:", e);
    } finally {
      setLoading(false);
    }
  };

  const fetchAccessoryCatalog = async (queryTerm) => {
    try {
      setLoadingProducts(true);
      const url = new URL("/api/data", window.location.origin);
      url.searchParams.set("scope", "hd_stock");
      url.searchParams.set("sub", "list");
      if (queryTerm && queryTerm.trim()) {
        url.searchParams.set("q", queryTerm.trim());
      }
      const res = await fetch(url.toString());
      const data = await res.json();
      if (data.ok) {
        setMatchingProducts(data.rows || []);
      }
    } catch (err) {
      console.error("Failed to query catalog items:", err);
    } finally {
      setLoadingProducts(false);
    }
  };

  const handleAddStandaloneAccessory = async () => {
    if (!selectedAccessory) {
      setActionError("Vui lòng chọn 1 sản phẩm phụ kiện.");
      return;
    }
    setActionError("");
    try {
      const res = await fetch("/api/data?scope=order_log&action=save_standalone_accessory", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          order_ref: filterRef,
          article_code: selectedAccessory.article_code,
          qty: Number(accessoryQty) || 1
        })
      });
      const data = await res.json();
      if (data.ok) {
        setAccessoryOpen(false);
        setSelectedAccessory(null);
        setSearchQuery("");
        setAccessoryQty(1);
        await loadCart();
      } else {
        setActionError(data.error || "Transaction error occur.");
      }
    } catch (err) {
      setActionError("Lỗi kết nối máy chủ.");
    }
  };

  const toggleSelect = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedIds(next);
  };

  const handleRefChange = (id, newVal) => {
    setRows(prev => prev.map(r => r.order_id === id ? { ...r, order_ref: newVal } : r));
  };

  const saveRef = async (id, val) => {
    try {
      await fetch("/api/data", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          scope: "order_log",
          action: "update_ref",
          order_id: id,
          order_ref: val
        })
      });
    } catch (e) {
      console.error("Save ref failed", e);
    }
  };

  // Batch assign quotes for the currently filtered order_ref
  const handleAssignSalesPerson = async (staffCode) => {
    if (!filterRef) return;
    try {
      setLoading(true);
      const res = await fetch("/api/data?scope=order_log&action=assign_salesperson", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          order_ref: filterRef,
          sales_person: staffCode
        })
      });
      const data = await res.json();
      if (data.ok) {
        setSelectedStaffForRef(staffCode);
        await loadCart();
      } else {
        alert("Lỗi khi gán nhân viên kinh doanh: " + (data.error || "Unknown"));
      }
    } catch (err) {
      alert("Lỗi kết nối khi gán nhân viên: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Open Monthly Conversion Rate Report with Month Filter
  const handleOpenReport = async (monthVal = reportMonth) => {
    setReportModalOpen(true);
    setReportLoading(true);
    try {
      const mParam = monthVal ? `&month=${monthVal}` : "";
      const res = await fetch(`/api/data?scope=order_log&action=conversion_report${mParam}`);
      const data = await res.json();
      if (data.ok) {
        setReportData(data);
      } else {
        alert("Lỗi tải báo cáo: " + (data.error || "Unknown"));
      }
    } catch (err) {
      alert("Lỗi kết nối khi tải báo cáo: " + err.message);
    } finally {
      setReportLoading(false);
    }
  };

  // Reconcile and synchronize all historical date ranges into sys_app_user
  const handleSyncToSysAppUser = async () => {
    if (!window.confirm("Bạn có chắc muốn đồng bộ toàn bộ doanh số và báo giá từ trước đến nay vào bảng nhân sự sys_app_user?")) return;
    setSyncingAppUser(true);
    try {
      const res = await fetch("/api/data?scope=order_log&action=sync_app_user", {
        method: "POST",
        headers: { "Content-Type": "application/json" }
      });
      const data = await res.json();
      if (data.ok) {
        alert("✅ " + data.message);
        await handleOpenReport(reportMonth);
      } else {
        alert("Lỗi đồng bộ: " + (data.error || "Unknown"));
      }
    } catch (err) {
      alert("Lỗi kết nối máy chủ khi đồng bộ: " + err.message);
    } finally {
      setSyncingAppUser(false);
    }
  };

  const sortedRows = useMemo(() => {
    const sorted = [...rows];
    sorted.sort((a, b) => {
      const spA = (a.salesperson_name || a.sales_person || "").toString();
      const spB = (b.salesperson_name || b.sales_person || "").toString();
      if (spA !== spB) return spA.localeCompare(spB);
      return b.order_id - a.order_id;
    });
    return sorted;
  }, [rows]);

  const uniqueRefs = useMemo(() => {
    const refs = new Set();
    rows.forEach(r => {
      const val = (r.order_ref || "").trim();
      if (val) refs.add(val);
    });
    return Array.from(refs).sort();
  }, [rows]);

  const visibleRows = useMemo(() => {
    if (!filterRef) return sortedRows;
    return sortedRows.filter(r => (r.order_ref || "").trim() === filterRef);
  }, [sortedRows, filterRef]);

  // Synchronize current salesperson value for the active filterRef
  useEffect(() => {
    if (filterRef && visibleRows.length > 0) {
      const currentSp = visibleRows[0].sales_person || visibleRows[0].salesperson_code || "";
      setSelectedStaffForRef(currentSp);
    } else {
      setSelectedStaffForRef("");
    }
  }, [filterRef, visibleRows]);

  const allVisibleSelected = visibleRows.length > 0 && visibleRows.every(r => selectedIds.has(r.order_id));

  const handleToggleAll = () => {
    const next = new Set(selectedIds);
    if (allVisibleSelected) {
      visibleRows.forEach(r => next.delete(r.order_id));
    } else {
      visibleRows.forEach(r => next.add(r.order_id));
    }
    setSelectedIds(next);
  };

  const handleDelete = async () => {
    const count = selectedIds.size;
    if (count === 0) return;
    if (!window.confirm(`Bạn có chắc muốn xóa ${count} dòng đã chọn khỏi giỏ hàng?\nAre you sure you want to delete ${count} selected items?`)) return;

    setLoading(true);
    try {
      const res = await fetch("/api/data", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          scope: "order_log",
          action: "delete",
          ids: Array.from(selectedIds),
        }),
      });
      const j = await res.json();
      if (j.ok) {
        setSelectedIds(new Set());
        await loadCart();
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleEstimateShipping = () => {
    if (!isPurchaser) {
      alert("KTT đã quyết định rằng tất cả các liên quan đến giá phải thông qua Bộ phận Thu mua (Purchaser) hoặc Quản trị viên Kinh doanh (Sales Admin). Vì vậy, vui lòng gửi yêu cầu của bạn qua email cho Purchaser/Admin.");
      return;
    }

    setEstimating(true);
    setTimeout(() => {
        const selectedItems = rows.filter(r => selectedIds.has(r.order_id));
        if (selectedItems.length === 0) {
            alert("Vui lòng chọn ít nhất 1 đơn hàng.");
            setEstimating(false);
            return;
        }
        // Bypass standalone accessories (prod_id 933) from the shipment validation check
        const majorItemsForShipping = selectedItems.filter(item => Number(item.prod_id) !== 933);
        const uniqueOrigins = new Set(majorItemsForShipping.map(item => item.origin));
        if (uniqueOrigins.size > 1) {
            const originsList = Array.from(uniqueOrigins).join(", ");
            alert(`⚠️ LỖI: Bạn đang trộn các đơn hàng từ nhiều nguồn khác nhau (${originsList})!`);
            setEstimating(false); 
            return;
        }

        let finalizedShippingItems = [...selectedItems];
        if (majorItemsForShipping.length > 0) {
          const majorOriginName = majorItemsForShipping[0].origin;
          const majorOriginId = majorItemsForShipping[0].origin_id;

          finalizedShippingItems = selectedItems.map(item => {
            if (Number(item.prod_id) === 933) {
              return {
                ...item,
                origin: majorOriginName,
                origin_id: majorOriginId
              };
            }
            return item;
          });
        }

        setItemsToPack(finalizedShippingItems);
        setShowBulkModal(true);
    }, 50);
  };

  // Convert to PO: atomically marks sold = true and navigates to Finished Goods PO
  const handleAddToPO = async () => {
    const selectedItems = rows.filter(r => selectedIds.has(r.order_id));
    if (selectedItems.length === 0) {
      alert("Vui lòng chọn ít nhất 1 mặt hàng để chuyển sang Đơn mua hàng.\nPlease select at least 1 item to pass to the PO module.");
      return;
    }

    // Isolate major lines away from standalone accessories (prod_id 933) for single vendor checking
    const majorItemsForPO = selectedItems.filter(item => Number(item.prod_id) !== 933);
    const uniqueOrigins = new Set(majorItemsForPO.map(item => item.origin_id));
    if (uniqueOrigins.size > 1) {
      alert("⚠️ CẢNH BÁO: Không được trộn lẫn nhiều nhà cung cấp/xuất xứ khác nhau trên một Đơn mua hàng!\nWarning: You cannot mix multiple source origins on a single Purchase Order!");
      return;
    }

    // Default to accessory group origin context if no major window treatment line items are selected
    const chosenOriginId = uniqueOrigins.size > 0 ? Array.from(uniqueOrigins)[0] : 933;

    // Execute atomic PO conversion to mark lines sold = true and update sys_app_user
    try {
      setLoading(true);
      const convRes = await fetch("/api/data?scope=order_log&action=convert_to_po", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ids: Array.from(selectedIds) })
      });
      const convData = await convRes.json();
      if (!convData.ok) {
        alert("Lỗi khi chuyển trạng thái đơn hàng: " + (convData.error || "Unknown"));
        return;
      }
    } catch (err) {
      alert("Lỗi kết nối máy chủ khi chuyển đổi đơn hàng: " + err.message);
      return;
    } finally {
      setLoading(false);
    }

    const transferLines = selectedItems.map((item, idx) => {
      const totalUnitExw = (Number(item.exwork) || 0) + (Number(item.ops_sc) || 0);
      const quantity = Number(item.qty) || 1;
      return {
        line_seq: idx + 1,
        prod_id: Number(item.prod_id),
        width_mm: Number(item.width_mm) || 0,
        height_mm: Number(item.height_mm) || 0,
        fab_collect: item.fab_collect || "",
        fab_color: item.fab_color || "",
        prod_ops: item.prod_ops || "",
        qty: quantity,
        unit_price: totalUnitExw, 
        line_total: quantity * totalUnitExw,
        notes: item.order_ref ? `Sales Ref: ${item.order_ref}` : "",
        order_id: item.order_id,
        config_snapshot: item.selected_config || null
      };
    });

    onClose();
    
    navigate("/purchase/finished-goods-order", {
      state: {
        incomingLines: transferLines,
        incomingSellerId: chosenOriginId
      }
    });
  };

  return (
    <>
    <Modal
      open={open}
      onClose={onClose}
      title="Giỏ đơn hàng / Order Cart"
      maxWidth={1240}
      headerExtra={
        <div style={{ display: "flex", gap: 6 }}>
          <Button 
            style={{ padding: "4px 8px", fontSize: 13, background: "#0f766e", color: "#fff", borderColor: "#0f766e", fontWeight: "600" }} 
            onClick={handleOpenReport}
          >
            📊 Báo cáo Chuyển đổi / Conversion Report
          </Button>
          <Button 
            style={{ marginRight: 8, padding: "4px 8px", fontSize: 13 }} 
            onClick={() => setGuideOpen(true)}
          >
            HDSD
          </Button>
        </div>
      }
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <div style={{ fontSize: "13px", color: "#666" }}>
          Hiển thị: <b>{visibleRows.length}</b> / {rows.length} đơn hàng
          {isPurchaser && <span style={{marginLeft: 10, color: "green", fontWeight: "bold"}}>[Admin View]</span>}
          
          {visibleRows.length > 0 && (
            <>
              <span style={{ margin: "0 10px", color: "#ddd" }}>|</span>
              <button onClick={handleToggleAll} style={{ background: "none", border: "none", color: "#007bff", fontWeight: 600, cursor: "pointer" }}>
                {allVisibleSelected ? "Bỏ chọn / Unselect" : "Chọn tất cả / Select All"}
              </button>
              
              <span style={{ margin: "0 10px", color: "#ddd" }}>|</span>
              <button 
                onClick={loadCart} 
                disabled={loading}
                style={{ background: "none", border: "none", color: "#28a745", fontWeight: 600, cursor: loading ? "default" : "pointer", opacity: loading ? 0.5 : 1 }}
              >
                {loading ? "Đang tải... / Loading..." : "Làm mới / Refresh"}
              </button>
            </>
          )}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {selectedIds.size > 0 && (
            <Button 
              type="button" 
              onClick={handleAddToPO}
              style={{ background: "#fff", color: "#ff4a4a", borderColor: "#ff4a4a", fontWeight: "700", padding: "4px 14px" }}
            >
              Add to PO
            </Button>
          )}
          
          {/* Dynamic action triggers bound to filterRef active constraints */}
          {filterRef && (
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              {/* Salesperson Batch-Assign Dropdown */}
              <div style={{ display: "flex", alignItems: "center", gap: 4, background: "#f0fdf4", border: "1px solid #bbf7d0", padding: "2px 8px", borderRadius: 6 }}>
                <label style={{ fontSize: "12px", fontWeight: 700, color: "#166534", whiteSpace: "nowrap" }}>
                  👤 Sales:
                </label>
                <select
                  className="text-input"
                  style={{ padding: "3px 6px", fontSize: "12px", minWidth: 140 }}
                  value={selectedStaffForRef}
                  onChange={(e) => handleAssignSalesPerson(e.target.value)}
                  disabled={loading}
                >
                  <option value="">-- Gán Salesperson --</option>
                  {salesStaff.map((st) => (
                    <option key={st.user_id} value={st.crm_id || st.user_id}>
                      {st.user_name} {st.crm_id ? `(${st.crm_id})` : ""}
                    </option>
                  ))}
                </select>
              </div>

              <Button 
                onClick={() => { setAccessoryOpen(true); fetchAccessoryCatalog(""); }}
                style={{ background: "#2563eb", color: "#fff", borderColor: "#1d4ed8", fontWeight: "600", padding: "4px 12px" }}
              >
                + Add Accessories
              </Button>

              {!isEditingExwork ? (
                <Button 
                  onClick={() => {
                    const initialMap = {};
                    visibleRows.forEach(r => {
                      const totalUnitExw = (Number(r.exwork) || 0) + (Number(r.ops_sc) || 0);
                      initialMap[r.order_id] = Number(totalUnitExw.toFixed(2));
                    });
                    setEditedExworkMap(initialMap);
                    setIsEditingExwork(true);
                  }}
                  style={{ background: "#d97706", color: "#fff", borderColor: "#b45309", fontWeight: "600", padding: "4px 12px" }}
                >
                  ✏️ Edit Exwork
                </Button>
              ) : (
                <>
                  <Button 
                    onClick={handleSaveExwork}
                    disabled={loading}
                    style={{ background: "#16a34a", color: "#fff", borderColor: "#15803d", fontWeight: "600", padding: "4px 12px" }}
                  >
                    💾 Save Exwork
                  </Button>
                  <Button 
                    onClick={() => { setIsEditingExwork(false); setEditedExworkMap({}); }}
                    disabled={loading}
                    style={{ background: "#6b7280", color: "#fff", borderColor: "#4b5563", fontWeight: "600", padding: "4px 10px" }}
                  >
                    Hủy
                  </Button>
                </>
              )}
            </div>
          )}

          <label style={{ fontSize: "13px", fontWeight: 600 }}>Lọc theo Ghi chú:</label>
          <select 
            className="text-input" 
            style={{ padding: "4px 8px", minWidth: 200 }}
            value={filterRef}
            onChange={(e) => {
              setFilterRef(e.target.value);
              setIsEditingExwork(false);
              setEditedExworkMap({});
            }}
          >
            <option value="">-- Tất cả / All --</option>
            {uniqueRefs.map((refVal, i) => (
              <option key={i} value={refVal}>{refVal}</option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <div style={{ padding: 20 }}>Đang tải...</div>
      ) : rows.length === 0 ? (
        <div style={{ padding: 20 }}>Giỏ hàng trống.</div>
      ) : (
        <div style={{ overflowX: "auto", maxHeight: "60vh" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
            <thead style={{ position: "sticky", top: 0, zIndex: 1 }}>
              <tr style={{ background: "#f5f5f5", borderBottom: "2px solid #ddd", textAlign: "left" }}>
                <th style={{ padding: 8, width: 30 }}>#</th>
                {isPurchaser && <th style={{ padding: 8, width: 130, color: "#007bff" }}>Salesperson</th>}
                <th style={{ padding: 8 }}>Sản phẩm</th>
                <th style={{ padding: 8 }}>Collection / Color</th>
                <th style={{ padding: 8 }}>Vận hành</th>
                <th style={{ padding: 8, width: 80 }}>Origin</th>
                <th style={{ padding: 8, width: 80 }}>R x C</th>
                <th style={{ padding: 8, width: 40 }}>Qty</th>
                {isPurchaser && <th style={{ padding: 8, width: 80, color: "#007bff" }}>Exwork</th>}
                <th style={{ padding: 8, width: 150 }}>Ghi chú</th>
              </tr>
            </thead>
            <tbody>
               {visibleRows.map((r) => {
                  const isSelected = selectedIds.has(r.order_id);
                  const originVal = r.origin || "Unknown"; 
                  
                  let originColor = "#f5f5f5"; 
                  if (originVal === "Malaysia") originColor = "#e3f2fd"; 
                  else if (originVal === "China") originColor = "#fff3e0"; 
                  else if (originVal === "USA") originColor = "#e8f5e9"; 
                  
                  const displayProdLabel = (r.prod_label || "").split("—")[0].trim();
                  const fabricDisplay = [r.fab_collect, r.fab_color].filter(Boolean).join(" / ");
                  const opsDisplay = r.prod_ops || "—";
                  const spDisplayName = r.salesperson_name || r.sales_person || (r.username || r.user_id || "N/A").split("@")[0];

                  return (
                    <tr key={r.order_id} style={{ borderBottom: "1px solid #eee", background: isSelected ? "#f0f8ff" : "#fff" }}>
                      <td style={{ padding: 8 }}>
                        <input type="checkbox" checked={isSelected} onChange={() => toggleSelect(r.order_id)}/>
                      </td>
                      {isPurchaser && (
                        <td style={{ padding: 8, color: "#222", fontSize: "12px", fontWeight: "600" }}>
                          <div>{spDisplayName}</div>
                          {r.sold && (
                            <span style={{ fontSize: "10px", background: "#dcfce7", color: "#166534", padding: "1px 5px", borderRadius: "3px", fontWeight: "bold", display: "inline-block", marginTop: 2 }}>
                              ✓ Đã bán / Sold
                            </span>
                          )}
                        </td>
                      )}
                      <td style={{ padding: 8 }}>
                        <div style={{ fontWeight: 500 }}>{displayProdLabel}</div>
                      </td>
                      <td style={{ padding: 8 }}>
                        <div style={{ fontSize: "12px", color: "#444" }}>{fabricDisplay || "—"}</div>
                      </td>
                      <td style={{ padding: 8 }}>
                        <div style={{ fontSize: "11px", fontWeight: 600, color: "#007bff" }}>{opsDisplay}</div>
                      </td>
                      <td style={{ padding: 8 }}>
                        <span style={{ background: originColor, padding: "2px 6px", borderRadius: "4px", fontSize: "11px", border: "1px solid #ddd", fontWeight: 600 }}>
                            {originVal}
                        </span>
                      </td>
                      <td style={{ padding: 8 }}>{r.width_mm === 0 && r.height_mm === 0 ? "Loose" : `${r.width_mm} x ${r.height_mm}`}</td>
                      <td style={{ padding: 8 }}>{r.qty}</td>
                      {isPurchaser && (
                        <td style={{ padding: 8, fontWeight: "600", color: "#2563eb" }}>
                          {isEditingExwork ? (
                            <div style={{ display: "flex", alignItems: "center" }}>
                              <span style={{ marginRight: 2 }}>$</span>
                              <input
                                type="number"
                                step="0.01"
                                className="text-input"
                                style={{ width: 85, padding: "2px 6px", fontWeight: "600", color: "#2563eb" }}
                                value={editedExworkMap[r.order_id] ?? (((Number(r.exwork) || 0) + (Number(r.ops_sc) || 0)).toFixed(2))}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  setEditedExworkMap(prev => ({ ...prev, [r.order_id]: val }));
                                }}
                              />
                            </div>
                          ) : (
                            r.exwork != null ? `$${((Number(r.exwork) || 0) + (Number(r.ops_sc) || 0)).toFixed(2)}` : "—"
                          )}
                        </td>
                      )}
                       <td style={{ padding: 8 }}>
                        <input
                          type="text"
                          className="text-input"
                          value={r.order_ref || ""}
                          onChange={(e) => handleRefChange(r.order_id, e.target.value)}
                          onBlur={(e) => saveRef(r.order_id, e.target.value)}
                        />
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ marginTop: 16, display: "flex", justifyContent: "space-between", alignItems: "center", borderTop: "1px solid #eee", paddingTop: 10 }}>
        <div style={{ fontSize: 13 }}>
          Đã chọn: <b>{selectedIds.size}</b> mục
        </div>
        <div style={{ display: "flex", gap: 8 }}>
           <Button
             disabled={selectedIds.size === 0 || loading || estimating}
             onClick={handleDelete}
             style={{ background: "#ffebee", color: "#c62828", border: "1px solid #ef9a9a", opacity: (selectedIds.size === 0 || loading || estimating) ? 0.5 : 1 }}
           >
             Xóa / Delete
           </Button>
           <Button disabled={selectedIds.size === 0 || estimating || loading} onClick={handleEstimateShipping}>
             {estimating ? "Đang xử lý..." : "Tính phí vận chuyển / Estimate Bulk Shipping"}
           </Button>
        </div>
      </div>
    </Modal>

    {/* ====================================================================== */}
    {/* EMBEDDED MODAL MODULE: MONTHLY CONVERSION RATE REPORT                  */}
    {/* ====================================================================== */}
    <Modal
      open={reportModalOpen}
      title="Báo cáo Tỷ lệ Chuyển đổi Báo giá / Monthly Quotation Conversion Report"
      onClose={() => setReportModalOpen(false)}
      maxWidth={1040}
    >
      {/* Top Controls: Month Selector, Sync Button, Export PDF Button */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, background: "#f8fafc", padding: "10px 14px", borderRadius: 8, border: "1px solid #e2e8f0", flexWrap: "wrap", gap: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <label style={{ fontWeight: 700, fontSize: 13, color: "#1e293b" }}>📅 Chọn Tháng Báo Cáo / Month:</label>
          <select
            className="text-input"
            style={{ padding: "4px 10px", fontWeight: 600, fontSize: 13, minWidth: 160 }}
            value={reportMonth}
            onChange={(e) => {
              const nextM = e.target.value;
              setReportMonth(nextM);
              handleOpenReport(nextM);
            }}
          >
            <option value="all">Tất cả các tháng / All Months</option>
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((m) => {
              const hasData = reportData?.available_months?.includes(m);
              return (
                <option key={m} value={m}>
                  Tháng {m} {hasData ? "● (Có dữ liệu)" : ""}
                </option>
              );
            })}
          </select>
        </div>

        <div style={{ display: "flex", gap: 8 }}>
          <Button
            onClick={handleExportPDF}
            disabled={!reportData || reportLoading}
            style={{ background: "#475569", color: "#fff", borderColor: "#334155", fontWeight: 600, fontSize: 12, padding: "5px 12px" }}
          >
            📄 Xuất PDF / Export PDF
          </Button>

          <Button
            onClick={() => setExcelExportModalOpen(true)}
            disabled={!reportData || reportLoading}
            style={{ background: "#16a34a", color: "#fff", borderColor: "#15803d", fontWeight: 600, fontSize: 12, padding: "5px 12px" }}
          >
            📊 Xuất Excel / Export Excel
          </Button>

          <Button
            onClick={handleSyncToSysAppUser}
            disabled={syncingAppUser || reportLoading}
            style={{ background: "#0284c7", color: "#fff", borderColor: "#0369a1", fontWeight: 600, fontSize: 12, padding: "5px 12px" }}
          >
            {syncingAppUser ? "Đang đồng bộ..." : "🔄 Đồng bộ vào sys_app_user"}
          </Button>
        </div>
      </div>

      {reportLoading ? (
        <div style={{ padding: 24, textAlign: "center" }}>Đang tính toán dữ liệu chuyển đổi... / Calculating metrics...</div>
      ) : !reportData ? (
        <div style={{ padding: 24, textAlign: "center", color: "#888" }}>Không có dữ liệu báo cáo / No report data available.</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {/* Executive KPI Cards */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12 }}>
            <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, padding: "12px 14px" }}>
              <div style={{ fontSize: 12, color: "#64748b", fontWeight: 600 }}>TỔNG BÁO GIÁ / TOTAL QUOTED</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: "#0f172a", marginTop: 4 }}>
                ${(reportData.summary.total_quoted_value || 0).toLocaleString()}
              </div>
              <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>
                {reportData.summary.total_quotes} yêu cầu báo giá
              </div>
            </div>

            <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 8, padding: "12px 14px" }}>
              <div style={{ fontSize: 12, color: "#166534", fontWeight: 600 }}>DOANH SỐ ĐÃ BÁN / TOTAL SOLD</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: "#15803d", marginTop: 4 }}>
                ${(reportData.summary.total_sold_value || 0).toLocaleString()}
              </div>
              <div style={{ fontSize: 11, color: "#16a34a", marginTop: 2 }}>
                {reportData.summary.sold_quotes} đơn chốt thành công
              </div>
            </div>

            <div style={{ background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 8, padding: "12px 14px" }}>
              <div style={{ fontSize: 12, color: "#1e40af", fontWeight: 600 }}>WIN RATE (SỐ LƯỢNG)</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: "#2563eb", marginTop: 4 }}>
                {reportData.summary.win_rate_vol_pct}%
              </div>
              <div style={{ fontSize: 11, color: "#3b82f6", marginTop: 2 }}>
                Tỷ lệ chốt trên tổng báo giá
              </div>
            </div>

            <div style={{ background: "#faf5ff", border: "1px solid #e9d5ff", borderRadius: 8, padding: "12px 14px" }}>
              <div style={{ fontSize: 12, color: "#6b21a8", fontWeight: 600 }}>REVENUE YIELD (GIÁ TRỊ)</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: "#9333ea", marginTop: 4 }}>
                {reportData.summary.revenue_yield_val_pct}%
              </div>
              <div style={{ fontSize: 11, color: "#a855f7", marginTop: 2 }}>
                Hiệu suất giá trị sản xuất
              </div>
            </div>
          </div>

          {/* Table Header Extra: Expand All / Collapse All */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: "#334155" }}>
              Chi tiết theo Nhân viên & Báo giá ({reportData.rows.length} nhân sự)
            </span>
            <button
              onClick={handleToggleExpandAll}
              style={{ background: "none", border: "none", color: "#0284c7", fontWeight: 600, fontSize: 12, cursor: "pointer" }}
            >
              {expandedStaff.size === reportData.rows.length ? "▲ Thu gọn tất cả / Collapse All" : "▼ Mở rộng tất cả chi tiết / Expand All"}
            </button>
          </div>

          {/* Detailed Staff Breakdown Table with Nested Sub-list */}
          <div style={{ border: "1px solid #e5e7eb", borderRadius: 8, overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ background: "#f9fafb", borderBottom: "2px solid #e5e7eb", textAlign: "left" }}>
                  <th style={{ padding: "10px 12px", width: "24%" }}>Nhân viên Kinh doanh</th>
                  <th style={{ padding: "10px 12px", width: "14%" }}>Mã NS (CRM)</th>
                  <th style={{ padding: "10px 12px", textAlign: "center", width: "13%" }}>Số đơn (Chốt / Tổng)</th>
                  <th style={{ padding: "10px 12px", textAlign: "right", width: "11%" }}>Win Rate (%)</th>
                  <th style={{ padding: "10px 12px", textAlign: "right", width: "14%" }}>Tổng Báo Giá (USD)</th>
                  <th style={{ padding: "10px 12px", textAlign: "right", width: "12%" }}>Đã Bán (USD)</th>
                  <th style={{ padding: "10px 12px", textAlign: "right", width: "12%" }}>Revenue Yield (%)</th>
                </tr>
              </thead>
              <tbody>
                {reportData.rows.map((row) => {
                  const staffKey = row.ma_nhan_su !== "N/A" ? row.ma_nhan_su : row.salesperson_name;
                  const isExpanded = expandedStaff.has(staffKey);
                  return (
                    <React.Fragment key={staffKey}>
                      <tr 
                        onClick={() => toggleExpandStaff(staffKey)}
                        style={{ borderBottom: "1px solid #f3f4f6", cursor: "pointer", background: isExpanded ? "#f8fafc" : "#fff" }}
                      >
                        <td style={{ padding: "10px 12px", fontWeight: 600, color: "#111827" }}>
                          <span style={{ marginRight: 6, color: "#64748b", fontSize: 11 }}>{isExpanded ? "▼" : "▶"}</span>
                          {row.salesperson_name}
                        </td>
                        <td style={{ padding: "10px 12px", color: "#6b7280" }}>
                          {row.ma_nhan_su}
                        </td>
                        <td style={{ padding: "10px 12px", textAlign: "center" }}>
                          <span style={{ fontWeight: 700, color: "#16a34a" }}>{row.sold_quotes}</span> / {row.total_quotes}
                        </td>
                        <td style={{ padding: "10px 12px", textAlign: "right", fontWeight: 700, color: "#2563eb" }}>
                          {row.win_rate_vol_pct}%
                        </td>
                        <td style={{ padding: "10px 12px", textAlign: "right" }}>
                          ${row.total_quoted_value.toLocaleString()}
                        </td>
                        <td style={{ padding: "10px 12px", textAlign: "right", fontWeight: 700, color: "#15803d" }}>
                          ${row.total_sold_value.toLocaleString()}
                        </td>
                        <td style={{ padding: "10px 12px", textAlign: "right", fontWeight: 700, color: "#9333ea" }}>
                          {row.revenue_yield_val_pct}%
                        </td>
                      </tr>

                      {/* Nested Sub-table: List of Quotation Order References & Full Line Item Details */}
                      {isExpanded && (
                        <tr style={{ background: "#f8fafc" }}>
                          <td colSpan={7} style={{ padding: "6px 12px 14px 28px", borderBottom: "1px solid #e2e8f0" }}>
                            <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 6, padding: "10px 14px" }}>
                              <div style={{ fontSize: 12, fontWeight: 700, color: "#475569", marginBottom: 8 }}>
                                📋 Chi tiết các Báo giá / Quotes under {row.salesperson_name}:
                              </div>
                              {row.quotes && row.quotes.length > 0 ? (
                                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                                  {row.quotes.map((q, qIdx) => (
                                    <div key={qIdx} style={{ border: "1px solid #cbd5e1", borderRadius: 6, overflow: "hidden" }}>
                                      {/* Quotation Header Bar */}
                                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: "#f1f5f9", padding: "6px 10px", fontSize: 12, fontWeight: 600 }}>
                                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                          <span style={{ color: "#64748b" }}>#{qIdx + 1}</span>
                                          <span style={{ color: "#0f172a", fontSize: 13, fontWeight: 700 }}>Mã: {q.order_ref}</span>
                                          <span style={{ fontSize: 11, color: "#64748b", fontWeight: 400 }}>({q.quote_date ? String(q.quote_date).slice(0, 10) : "—"})</span>
                                        </div>
                                        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                                          <span style={{ fontSize: 11, color: "#475569" }}>{q.lines_count} dòng SP</span>
                                          {q.is_sold ? (
                                            <span style={{ background: "#dcfce7", color: "#166534", padding: "2px 6px", borderRadius: 4, fontWeight: 700, fontSize: 11 }}>
                                              ✓ Đã bán / Sold
                                            </span>
                                          ) : (
                                            <span style={{ background: "#fff7ed", color: "#c2410c", padding: "2px 6px", borderRadius: 4, fontWeight: 600, fontSize: 11 }}>
                                              Đang báo giá
                                            </span>
                                          )}
                                          <span style={{ color: "#0f172a", fontWeight: 700 }}>${Number(q.quoted_value || 0).toLocaleString()}</span>
                                        </div>
                                      </div>

                                      {/* Quotation Product Line Items Table */}
                                      {q.items && q.items.length > 0 && (
                                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11.5 }}>
                                          <thead>
                                            <tr style={{ background: "#fafafa", borderBottom: "1px solid #e2e8f0", color: "#64748b", textAlign: "left" }}>
                                              <th style={{ padding: "5px 8px", width: 25 }}>#</th>
                                              <th style={{ padding: "5px 8px" }}>Sản phẩm</th>
                                              <th style={{ padding: "5px 8px" }}>Collection / Color</th>
                                              <th style={{ padding: "5px 8px" }}>Vận hành</th>
                                              <th style={{ padding: "5px 8px", textAlign: "center" }}>R x C (mm)</th>
                                              <th style={{ padding: "5px 8px", textAlign: "center" }}>SL</th>
                                              <th style={{ padding: "5px 8px", textAlign: "right" }}>Thành tiền (USD)</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {q.items.map((it, itIdx) => (
                                              <tr key={it.order_id || itIdx} style={{ borderBottom: "1px solid #f1f5f9" }}>
                                                <td style={{ padding: "5px 8px", color: "#94a3b8" }}>{itIdx + 1}</td>
                                                <td style={{ padding: "5px 8px", fontWeight: 500, color: "#1e293b" }}>{it.prod_label}</td>
                                                <td style={{ padding: "5px 8px", color: "#475569" }}>{it.fabric_display}</td>
                                                <td style={{ padding: "5px 8px", color: "#0284c7", fontWeight: 600 }}>{it.prod_ops}</td>
                                                <td style={{ padding: "5px 8px", textAlign: "center" }}>{it.dimensions}</td>
                                                <td style={{ padding: "5px 8px", textAlign: "center" }}>{it.qty}</td>
                                                <td style={{ padding: "5px 8px", textAlign: "right", fontWeight: 600, color: "#0f172a" }}>
                                                  ${Number(it.line_total || 0).toLocaleString()}
                                                </td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <div style={{ fontSize: 12, color: "#888", padding: "6px 0" }}>Chưa có báo giá nào được ghi nhận cho thời kỳ này.</div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
                {reportData.rows.length === 0 && (
                  <tr>
                    <td colSpan={7} style={{ padding: 16, textAlign: "center", color: "#888" }}>
                      Chưa có phát sinh báo giá nào được ghi nhận cho thời kỳ này.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Vietnamese Operational Explanatory Notes */}
          <div style={{ background: "#fffbeb", border: "1px solid #fef3c7", borderRadius: 8, padding: "12px 14px", fontSize: 12, color: "#92400e", lineHeight: 1.5 }}>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>📋 Ghi chú vận hành quản lý / Management Guidelines:</div>
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              <li>
                <b>Win Rate (Tỷ lệ thắng theo số lượng):</b> Tỷ lệ phần trăm số lượng báo giá được chốt thành đơn hàng thực tế (<code>sold = true</code>) trên tổng số lượt khách gửi yêu cầu báo giá.
              </li>
              <li>
                <b>Revenue Yield (Hiệu suất doanh thu theo giá trị):</b> Tỷ lệ phần trăm giữa tổng giá trị sản xuất thực tế chốt bán thành công so với tổng giá trị sản xuất đã báo giá ban đầu.
              </li>
              <li>
                <b>Định giá chuẩn sản xuất:</b> Giá trị sản phẩm chỉ ghi nhận giá trị xuất xưởng thực tế tại nhà máy <code>(exwork + ops_sc) * qty</code>. Toàn bộ chi phí cước vận chuyển (<code>ship_sc</code>) được tách biệt hoàn toàn và đối soát riêng sau khi giao hàng.
              </li>
            </ul>
          </div>
        </div>
      )}
    </Modal>

    {/* ====================================================================== */}
    {/* EMBEDDED MODAL MODULE: STANDALONE ACCESSORIES TYPE-AHEAD SELECTOR     */}
    {/* ====================================================================== */}
    <Modal
      open={accessoryOpen}
      title={`Add Accessories to Order Ref: [ ${filterRef} ]`}
      onClose={() => { setAccessoryOpen(false); setSelectedAccessory(null); setSearchQuery(""); }}
      maxWidth={650}
      footer={
        <div style={{ display: "flex", gap: 8, width: "100%", justifyContent: "flex-end", alignItems: "center" }}>
          {selectedAccessory && (
            <div style={{ marginRight: "auto", display: "flex", alignItems: "center", gap: 8 }}>
              <label style={{ fontWeight: "600", fontSize: "13px" }}>Quantity:</label>
              <input 
                type="number" 
                className="text-input" 
                style={{ width: 70, padding: "4px 8px" }} 
                value={accessoryQty} 
                min="1" 
                onChange={(e) => setAccessoryQty(Math.max(1, parseInt(e.target.value) || 1))} 
              />
            </div>
          )}
          <Button 
            onClick={handleAddStandaloneAccessory}
            disabled={!selectedAccessory || loadingProducts}
            style={{ background: "#28a745", color: "#fff", borderColor: "#1e7e34", fontWeight: "600" }}
          >
            Add to Order
          </Button>
        </div>
      }
    >
      <div style={{ marginBottom: 12 }}>
        <label style={{ display: "block", fontWeight: 600, marginBottom: 6 }}>
          Search Stock Registry (Article Code / Collection Name)
        </label>
        <input 
          type="text"
          className="text-input"
          placeholder="Type unique item string variables to target lookups..."
          value={searchQuery}
          onChange={(e) => { setSearchQuery(e.target.value); setSelectedAccessory(null); }}
        />
        <div style={{ fontSize: "11px", opacity: 0.7, marginTop: 4 }}>
          {loadingProducts ? "Querying records..." : `Total matching items found: ${matchingProducts.length}`}
        </div>
      </div>

      <div style={{ border: "1px solid #ddd", borderRadius: 8, maxHeight: 300, overflowY: "auto", background: "#fafafa" }}>
        {matchingProducts.map((p, index) => {
          const isSelectedItem = selectedAccessory?.article_code === p.article_code;
          return (
            <div
              key={`${p.article_code}-${index}`}
              onClick={() => setSelectedAccessory(p)}
              style={{
                padding: "10px 12px",
                borderBottom: "1px solid #eee",
                cursor: "pointer",
                background: isSelectedItem ? "#eeffe6" : "#fff",
                transition: "background 0.15s ease"
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", fontWeight: "700" }}>
                <span>Code: {p.article_code}</span>
                <span style={{ color: "#2563eb" }}>EXW Cost: ${p.exw || 0}</span>
              </div>
              <div style={{ fontSize: "13px", color: "#555", margin: "2px 0" }}>
                {p.collection_name || "—"} {p.group_color ? `(Color: ${p.group_color})` : ""}
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: "11px", opacity: 0.7 }}>
                <span>Origin: {p.origin || "Unknown"} | UOM: {p.uom || "pcs"}</span>
                <span style={{ fontWeight: "600", color: "#166534" }}>RSP: ${p.retail_buy || 0}</span>
              </div>
            </div>
          );
        })}
        {matchingProducts.length === 0 && !loadingProducts && (
          <div style={{ padding: 16, textAlign: "center", color: "#888", fontSize: "13px" }}>
            No matching items found in warehouse master log registry.
          </div>
        )}
      </div>

      {actionError && (
        <div style={{ color: "crimson", marginTop: 12, fontWeight: 500, padding: "6px 10px", background: "#fef2f2", borderRadius: "6px", border: "1px solid #fca5a5", fontSize: "13px" }}>
          {actionError}
        </div>
      )}
    </Modal>

    <Suspense fallback={null}>
       {showBulkModal && (
           <BulkPackingModal open={showBulkModal} onClose={() => { setShowBulkModal(false); setEstimating(false); }} selectedItems={itemsToPack} />
       )}
    </Suspense>

    <Modal open={guideOpen} title={activeGuide.title} onClose={() => setGuideOpen(false)} maxWidth={600}>
        <div className="box" style={{ margin: 0, border: "none", padding: 0 }}>
            <div style={{ marginBottom: 16 }}>{activeGuide.intro}</div>
            <ul style={{ paddingLeft: 20, margin: 0 }}>
                {activeGuide.steps.map((line, i) => <li key={i} style={{ marginBottom: 12, whiteSpace: "pre-wrap" }}>{line}</li>)}
            </ul>
        </div>
    </Modal>

    {/* ====================================================================== */}
    {/* EMBEDDED MODAL MODULE: EXCEL EXPORT DETAIL/SUMMARY OPTION SELECTOR     */}
    {/* ====================================================================== */}
    <Modal
      open={excelExportModalOpen}
      title="Tùy chọn Xuất Excel / Excel Export Options"
      onClose={() => setExcelExportModalOpen(false)}
      maxWidth={480}
    >
      <div style={{ padding: "8px 0" }}>
        <p style={{ margin: "0 0 16px", fontSize: 13, color: "#475569" }}>
          Vui lòng chọn định dạng báo cáo muốn xuất ra file Excel:
        </p>

        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {/* Option 1: Summary Only */}
          <div
            onClick={() => {
              handleExportExcel(false);
              setExcelExportModalOpen(false);
            }}
            style={{
              border: "1px solid #cbd5e1",
              borderRadius: 8,
              padding: "12px 14px",
              cursor: "pointer",
              background: "#f8fafc",
              transition: "all 0.15s ease"
            }}
            onMouseEnter={(e) => (e.currentTarget.style.borderColor = "#0284c7")}
            onMouseLeave={(e) => (e.currentTarget.style.borderColor = "#cbd5e1")}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
              <span style={{ fontWeight: 700, fontSize: 13, color: "#0f172a" }}>
                📑 Báo cáo Tóm tắt (Chỉ dòng Order Ref)
              </span>
              <span style={{ fontSize: 11, background: "#e0f2fe", color: "#0369a1", padding: "2px 6px", borderRadius: 4, fontWeight: 600 }}>
                Summary
              </span>
            </div>
            <div style={{ fontSize: 12, color: "#64748b", lineHeight: 1.4 }}>
              Chỉ hiển thị tổng kết theo Nhân viên kinh doanh và tóm tắt từng mã Báo giá (Order Ref), ẩn chi tiết từng dòng sản phẩm.
            </div>
          </div>

          {/* Option 2: Full Detailed Report */}
          <div
            onClick={() => {
              handleExportExcel(true);
              setExcelExportModalOpen(false);
            }}
            style={{
              border: "1px solid #86efac",
              borderRadius: 8,
              padding: "12px 14px",
              cursor: "pointer",
              background: "#f0fdf4",
              transition: "all 0.15s ease"
            }}
            onMouseEnter={(e) => (e.currentTarget.style.borderColor = "#16a34a")}
            onMouseLeave={(e) => (e.currentTarget.style.borderColor = "#86efac")}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
              <span style={{ fontWeight: 700, fontSize: 13, color: "#166534" }}>
                📊 Báo cáo Chi tiết Đầy đủ (Kèm chi tiết SP)
              </span>
              <span style={{ fontSize: 11, background: "#dcfce7", color: "#15803d", padding: "2px 6px", borderRadius: 4, fontWeight: 600 }}>
                Full Detailed
              </span>
            </div>
            <div style={{ fontSize: 12, color: "#166534", lineHeight: 1.4 }}>
              Bao gồm đầy đủ chi tiết từng sản phẩm (Tên SP, Collection, Màu, Hệ vận hành, Kích thước, SL, Đơn giá) dưới mỗi mã báo giá.
            </div>
          </div>
        </div>
      </div>
    </Modal>
    </>
  );
}